import React, {type ReactNode, useContext, useState} from 'react';

import TabbedModal, {
  StyledModalFooter,
} from 'app/components/general/TabbedModal';
import StateButton from 'app/general/widgets/StateButton';
import {gt} from 'app/utils/i18n';

import type {DeleteFolderProps} from 'app/folders/api';

export type SelectedItemType<T> = {
  object: T;
  help?: string;
  title?: string;
  icon?: string;
  description?: string;
  action: (item: T) => Promise<unknown>;
  callback?: void | (() => Promise<unknown>);
};

type KBTreeDeleteItemContextProviderProps = {
  children: ReactNode;
};

export const KBTreeDeleteItemContextProvider = ({
  children,
}: KBTreeDeleteItemContextProviderProps) => {
  const [selected, setSelected] =
    useState<SelectedItemType<DeleteFolderProps> | null>(null);

  const setDeleting = (selectedItem: SelectedItemType<DeleteFolderProps>) =>
    setSelected({...selectedItem});

  return (
    <KBDeleteItemContext.Provider value={{setDeleting, selected}}>
      {children}
      <KBTreeDeleteItemModal
        toDelete={selected}
        onClose={() => setSelected(null)}
      />
    </KBDeleteItemContext.Provider>
  );
};

type DeleteContextType = {
  setDeleting: (selectedItem: SelectedItemType<DeleteFolderProps>) => void;
  selected: SelectedItemType<DeleteFolderProps> | null;
};

export const KBDeleteItemContext = React.createContext<DeleteContextType>({
  setDeleting: () => undefined,
  selected: null,
});

export const useDeleteModal = () => {
  const {setDeleting} = useContext(KBDeleteItemContext);
  return setDeleting;
};

type KBTreeDeleteItemModalProps = {
  toDelete: SelectedItemType<DeleteFolderProps> | null;
  onClose: () => void;
};

export const KBTreeDeleteItemModal = ({
  toDelete,
  onClose,
}: KBTreeDeleteItemModalProps) => {
  const deleteObject = () => {
    if (toDelete == null) {
      return;
    }

    const {object, action, callback} = toDelete;
    return action(object).then(() => {
      if (callback != null) {
        return callback().then(onClose, onClose);
      }

      onClose();
    });
  };

  const name = toDelete != null ? <b>{toDelete.object.name}</b> : null;
  const title =
    toDelete != null && toDelete.title != null ? toDelete.title : gt('Delete');
  const icon =
    toDelete != null && toDelete.icon != null ? toDelete.icon : 'far fa-trash';
  const description =
    toDelete != null && toDelete.description != null
      ? toDelete.description
      : gt('Delete %(name)s?', {
          name,
        });
  const help =
    toDelete != null && toDelete.help ? (
      <p className="small">{toDelete.help}</p>
    ) : null;

  return (
    <TabbedModal
      show={toDelete != null}
      onCancel={onClose}
      tabs={[
        {
          eventKey: 'delete-object',
          name: title,
          icon,
          content: (
            <div>
              <p>{description}</p>
              {help}
              <StyledModalFooter>
                <button
                  onClick={onClose}
                  className="btn btn-link"
                  data-dismiss="modal"
                >
                  {gt('Cancel')}
                </button>
                <StateButton
                  onClick={deleteObject}
                  busyText={title}
                  className="btn btn-danger"
                  data-dismiss="modal"
                >
                  {title}
                </StateButton>
              </StyledModalFooter>
            </div>
          ),
        },
      ]}
    />
  );
};
